<script setup lang="ts">
import SingleOperationOfficeAutoComplete from "@/components/AutoCompletes/SingleOperationOfficeAutoComplete.vue"
import ParentDepartmentAutoComplete from "@/components/Department/ParentDepartmentAutoComplete.vue"
import getDepartmentAllParams from "@/types/responses/getDepartmentAllParams"
import getOperationOffice from "@/types/responses/getOperationOffice"
import getDepartmentResponse from "@/types/responses/getDepartment"
import SelectLevel from "@/components/Department/SelectLevel.vue"
import { DEPARTMENT_SELECTABLE_LEVEL } from "@/services/consts"
import { isSecondLevelDepartment } from "@/services/functions"
import FormRow from "@/components/Common/FormRow.vue"
import { computed } from "vue"
import i18n from "@/lang/i18n"

type formType = {
  operation_office: getOperationOffice | null
  level: { key: number, label: string }
  parent: getDepartmentResponse | null
  id_for_customer: string
  name: string
}

const emit = defineEmits(['update:modelValue']);
const props = defineProps<{
  modelValue: formType
  department: getDepartmentAllParams | null
  parentIdExistsDepartments: getDepartmentAllParams[]
  initialOperationId?: number
  isDepartment: boolean
  isChildDepartment: boolean
  isRequired: boolean
  errors: any
}>()

const modelValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit("update:modelValue", value);
  },
})

const isDisabledDepartment = computed(() => {
  if (!props.department) return false
  return !!props.parentIdExistsDepartments.filter((department) => {
    return department.parent_id == props.department?.id
  }).length
})

const selectableLevel = computed(() => {
  if (isDisabledDepartment.value) return [DEPARTMENT_SELECTABLE_LEVEL[0]]
  if (props.isRequired) return [DEPARTMENT_SELECTABLE_LEVEL[1]]
  return DEPARTMENT_SELECTABLE_LEVEL
})
</script>
<template>
  <FormRow
    :title="i18n.global.t('pages.departments.createOrUpdateModal.operationOffice')"
    :required="true"
    class="mt-10"
  >
    <SingleOperationOfficeAutoComplete
      v-model="modelValue.operation_office"
      :initialOperationId="props.department?.operation_office_id"
      :required="false"
      :errorMessages="props.errors.operation_office_id"
      :clearable="false"
      :readonly="props.isChildDepartment"
    />
  </FormRow>
  <FormRow
    :title="i18n.global.t('pages.departments.createOrUpdateModal.selectLevel')"
    :required="true"
  >
    <SelectLevel
      v-model="modelValue.level"
      :items="selectableLevel"
      :errorMessages="props.errors.level"
    />
  </FormRow>
  <FormRow
    v-if="isSecondLevelDepartment(modelValue.level.key)"
    :title="i18n.global.t('pages.departments.createOrUpdateModal.department')"
    :required="true"
  >
    <ParentDepartmentAutoComplete
      v-model="modelValue.parent"
      :operationOffice="modelValue.operation_office"
      :departmentId="props.department?.parent_id"
      :exceptionId="props.department?.id"
      :disabled="isDisabledDepartment"
      :isDepartment="props.isDepartment"
      :isChildDepartment="props.isChildDepartment"
      :errorMessages="props.errors.parent_id"
    />
  </FormRow>
  <div
    class="d-flex mt-4 px-3 mb-2"
  >
    <v-text-field
      v-model="modelValue.id_for_customer"
      :label="i18n.global.t('pages.departments.createOrUpdateModal.idForCustomer')"
      outlined
      dense
      class="mr-4"
      :error-messages="props.errors.id_for_customer"
      maxlength="255"
    />
    <v-text-field
      v-model="modelValue.name"
      :label="i18n.global.t('pages.departments.createOrUpdateModal.name')"
      outlined
      dense
      :error-messages="props.errors.name"
      maxlength="255"
    />
  </div>
</template>
